import helpIconUrl from '@/assets/icon-help.svg';
import { useTranslation } from '@/i18n/useTranslation';

export interface NeedHelpFabProps {
  /** Opens {@link NeedHelpDialog}. The pill itself raises nothing — the ask is the dialog's submit. */
  readonly onClick: () => void;
}

/**
 * The "Need help?" pill, anchored bottom-right from check-in onward [Figma frames].
 *
 * ⭐ **It has a destination now, and the destination is NOT KQA-1's panel.** This control was deliberately
 * inert, and the comment it carried said why: the FAQ / Q&A assistant behind it has no design and no
 * approved story. That is still true and still deferred [0169, KQA-1, KVE-1]. What changed is that 0169
 * also named the other half — *"the decorative help bubble is out of the MVP, and the functional help
 * control that enqueues a recruiter is in it. If only one ships, it must be [0168]'s"* — and a live, inert
 * pill on every screen from check-in onward was the dead control [0168] exists to remove.
 *
 * So it opens `NeedHelpDialog`: a name, and a representative. Not an assistant, and it does not pretend to
 * be one.
 *
 * The pill's own geometry is unchanged — this is the same element, minus `disabled`, plus a handler.
 */
export function NeedHelpFab({ onClick }: NeedHelpFabProps) {
  const t = useTranslation();

  return (
    <button
      className="kiosk-focusable fixed bottom-5 right-5 flex min-h-touch items-center gap-2 rounded-[var(--radius-full)] bg-[var(--primary)] px-4 py-3 shadow-[shadow:var(--elevation-3)]"
      onClick={onClick}
      type="button"
    >
      <img alt="" className="size-5" src={helpIconUrl} />
      <span className="text-sm font-semibold text-[var(--primary-foreground)]">
        {t('help.fab')}
      </span>
    </button>
  );
}
